import { useEffect, useState } from "react";
import { Loader2, Minus, Plus, Trash2 } from "lucide-react";
import { formatMoney } from "@/lib/shopify";
import { useAuth } from "@/hooks/useAuth";
import { adminTestCheckoutAllowed, cartSubtotalCents, linesByAthlete, prepareCheckoutUrl, useHerCollectionCart } from "@/stores/herCollectionCart";

/** SIIS-styled bag. Groups lines by athlete; checkout opens only for signed-in admins in Admin Preview during a controlled test. */
const HerCollectionBag = ({ preview = false }: { preview?: boolean }) => {
  const { lines, isLoading, error, setQuantity, removeLine, sync, cartId } = useHerCollectionCart();
  const { isAdmin } = useAuth();
  const [opening, setOpening] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const adminPreviewParam = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("adminPreview") === "1";
  const canCheckout = adminTestCheckoutAllowed({ isAdmin, preview, adminPreviewParam });
  useEffect(() => {
    sync();
  }, [sync]);

  const checkout = async () => {
    setCheckoutError(null);
    const tab = window.open("about:blank", "_blank");
    setOpening(true);
    const url = await prepareCheckoutUrl(cartId, canCheckout).catch(() => null);
    setOpening(false);
    if (url && tab) tab.location.href = url;
    else { tab?.close(); setCheckoutError("Couldn't open checkout. Please try again."); }
  };

  if (lines.length === 0)
    return <p className="text-sm text-muted-foreground" role="status">Your bag is empty.</p>;

  const groups = linesByAthlete(lines);
  const currency = lines[0].price.currencyCode;
  const subtotal = formatMoney({ amount: (cartSubtotalCents(lines) / 100).toFixed(2), currencyCode: currency });

  return (
    <div className="space-y-4" aria-busy={isLoading}>
      {Object.entries(groups).map(([athleteId, group]) => (
        <section key={athleteId} aria-label={`Items backing ${group[0].athleteName}`} className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">Backing {group[0].athleteName}</p>
          <ul className="space-y-2">
            {group.map((l) => (
              <li key={l.variantId} className="flex gap-3 rounded-xl border border-border p-2 min-w-0">
                <div className="w-16 h-16 shrink-0 rounded-lg bg-muted overflow-hidden">
                  {l.imageUrl && <img src={l.imageUrl} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold leading-tight break-words">{l.productTitle}</p>
                  <p className="text-xs text-muted-foreground">{l.selectedOptions.map((o) => o.value).join(" · ")}</p>
                  <p className="text-sm font-semibold mt-0.5">{formatMoney(l.price)}</p>
                </div>
                <div className="flex flex-col items-end justify-between gap-1 shrink-0">
                  <button type="button" aria-label={`Remove ${l.productTitle} ${l.variantTitle}`} disabled={isLoading}
                    onClick={() => removeLine(l.variantId)}
                    className="p-2 rounded-full hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50">
                    <Trash2 className="w-4 h-4" aria-hidden />
                  </button>
                  <div className="flex items-center gap-1">
                    <button type="button" aria-label="Decrease quantity" disabled={isLoading}
                      onClick={() => setQuantity(l.variantId, l.quantity - 1)}
                      className="w-8 h-8 rounded-full border border-border flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50">
                      <Minus className="w-3 h-3" aria-hidden />
                    </button>
                    <span className="w-6 text-center text-sm" aria-label={`Quantity ${l.quantity}`}>{l.quantity}</span>
                    <button type="button" aria-label="Increase quantity" disabled={isLoading}
                      onClick={() => setQuantity(l.variantId, l.quantity + 1)}
                      className="w-8 h-8 rounded-full border border-border flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50">
                      <Plus className="w-3 h-3" aria-hidden />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
      <div className="flex items-baseline justify-between border-t border-border pt-3">
        <span className="font-semibold">Subtotal</span>
        <span className="font-display text-xl" data-testid="bag-subtotal">{subtotal}</span>
      </div>
      <p className="text-xs text-muted-foreground">Taxes and shipping are calculated at checkout.</p>
      {checkoutError && <p className="text-sm text-destructive" role="alert">{checkoutError}</p>}
      <button type="button" disabled={!canCheckout || isLoading || opening} onClick={canCheckout ? checkout : undefined}
        className="w-full min-h-[48px] rounded-full bg-primary text-primary-foreground font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
        {isLoading || opening ? <Loader2 className="w-4 h-4 animate-spin mx-auto" aria-hidden /> : canCheckout ? "Test checkout (Admin Preview only)" : "Checkout isn't open yet"}
      </button>
    </div>
  );
};

export default HerCollectionBag;
